import { POLICY_IDS } from '@/features/decisions/config/personaContext';

// "What changes between policies?" content (issue #128, Max's revised spec).
// Editorial copy curated per policy — NOT API-derived — so it lives here next to
// POLICY_IDS/POLICY_NAMES as the single place to tune wording and lever states.

// The three optimization levers, shown as T/Q/S badges.
export type Lever = 'T' | 'Q' | 'S';

// Badge state: 'off' = lever not engaged (gray), 'incumbent' = the incumbent's
// baseline lever (gold), 'keystone' = Keystone-optimized (navy).
export type LeverState = 'off' | 'incumbent' | 'keystone';

export const LEVER_ORDER: Lever[] = ['T', 'Q', 'S'];

export const LEVER_LABELS: Record<Lever, string> = {
  T: 'Timing — replenishment cadence / cycle',
  Q: 'Quantity — order / run quantity',
  S: 'Service level',
};

export interface PolicyNarrative {
  // Optional sub-label under the canonical policy name (policyName); only the
  // Incumbent uses one now ("Periodic Production"). The Keystone names already
  // carry their descriptor (Quantity Only / Fully Optimized).
  descriptor?: string;
  levers: Record<Lever, LeverState>;
  // One-paragraph "what changes" explanation.
  blurb: string;
  // Dot color in the explanation column (progression gray → navy).
  dotColor: string;
}

// Keyed by experiment_id (the three persona policies). Unmapped policies simply
// render nothing in the panel.
export const POLICY_NARRATIVES: Record<string, PolicyNarrative> = {
  [POLICY_IDS.incumbent]: {
    descriptor: 'Periodic Production',
    levers: { T: 'off', Q: 'incumbent', S: 'off' },
    blurb: 'Fixed cycle, variable quantity sized off a mean (point) forecast — the periodic policy running today.',
    dotColor: '#cbd5e1',
  },
  [POLICY_IDS.keystoneForecast]: {
    levers: { T: 'off', Q: 'keystone', S: 'off' },
    blurb:
      "Same fixed cadence, but the run quantity is now re-sized from Keystone's full distributional forecast instead of the incumbent's mean. That single change drives the gain.",
    dotColor: '#64748b',
  },
  [POLICY_IDS.keystoneOptimized]: {
    levers: { T: 'keystone', Q: 'keystone', S: 'keystone' },
    blurb: 'Keystone optimizes quantity, cycle, and service level together.',
    dotColor: '#1e293b',
  },
};
